import React from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { connect, ConnectedProps } from 'react-redux';

import { TFunction } from 'react-i18next';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Text,
  Stack,
  StyledContainer,
  List,
  TextField,
  Form,
  Pagination,
  Button,
  Tooltip,
  withTranslation,
} from '@piwikpro/ui-components';
import debounce from 'lodash/debounce';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import {
  ConfigService,
  Pagination as PaginationManager,
  QueryParams,
  errorBoundary,
  withInject,
  InternalListLink,
} from '@piwikpro/platform';
import { MetaSites } from '@piwikpro/meta-sites-crate';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';

enum SortDirection {
  ASC = '-',
  DESC = ''
}

const mapStateToProps = (state: MetaSitesState & { session: SessionState }) => ({
  insideApps: state.metaSites.apps.inside.list,
  isLoading: state.metaSites.apps.inside.isLoading,
  failed: state.metaSites.apps.inside.failed,
  total: state.metaSites.apps.inside.total,
  userRole: state.session.userRole,
  metaSite: state.metaSites.details.settings.settings,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  metaSites: MetaSites
  userPreferences: any
  routerService: RouterService
}

interface Props extends InjectedProps, ReduxProps {
  routeParams: QueryParams
  pathname: string
  t: TFunction
}

interface State {
  searchDebounce: any
  search: string
  sort: {
    direction: SortDirection
    by: string
  }
}

class Apps extends React.Component<Props, State> {
  state = {
    searchDebounce: null,
    search: '',
    sort: {
      direction: SortDirection.DESC,
      by: 'cnil',
    },
  }

  private listPreference: any;

  constructor(props) {
    super(props);

    const { userPreferences } = this.props;

    this.listPreference = new ListPreference(userPreferences.load(), '');

    this.handleLimitChange = this.handleLimitChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleDeleteApp = this.handleDeleteApp.bind(this);
    this.handleSortingChange = this.handleSortingChange.bind(this);
  }

  async componentDidMount() {
    await this.fetchApps();
  }

  async componentDidUpdate(prevProps: Props) {
    const { routeParams } = this.props;

    if (
      routeParams.page !== prevProps.routeParams.page
      || routeParams.limit !== prevProps.routeParams.limit
    ) {
      await this.fetchApps();
    }
  }

  async fetchApps() {
    const { metaSites, metaSite, routeParams } = this.props;
    const { search, sort } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    try {
      await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
        search,
        limit: pagination.getLimit(),
        offset: pagination.offset,
        sort,
      });
    // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  async handleLimitChange(limit) {
    const { search, sort } = this.state;
    const {
      routerService,
      pathname,
      metaSites,
      userPreferences,
      metaSite,
    } = this.props;

    try {
      await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
        search,
        limit,
        offset: 0,
        sort,
      });

      this.listPreference.limit = limit;

      userPreferences.persist(this.listPreference);

      routerService.push({ to: buildPaginatedPath(pathname, 1, limit) });
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  async handlePageChange(offset, pagination) {
    const { search, sort } = this.state;
    const {
      routerService, pathname, metaSites, metaSite,
    } = this.props;

    try {
      await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
        search,
        limit: pagination.getLimit(),
        offset,
        sort,
      });

      routerService.push({
        to: buildPaginatedPath(
          pathname,
          pagination.getPageFromOffset(offset),
          pagination.getLimit(),
        ),
      });
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  async handleSortingChange(by) {
    const { metaSites, routeParams, metaSite } = this.props;
    const { search, sort } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    const direction = sort.direction === '' ? SortDirection.ASC : SortDirection.DESC;

    try {
      await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
        search,
        limit: pagination.getLimit(),
        offset: pagination.offset,
        sort: { direction, by },
      });
    // eslint-disable-next-line no-empty
    } catch (err) {}

    this.setState({
      sort: {
        by,
        direction,
      },
    });
  }

  handleSearchChange(event) {
    const { searchDebounce } = this.state;
    const {
      metaSites,
      routeParams,
      config,
      routerService,
      pathname,
      metaSite,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    if (searchDebounce) {
      (searchDebounce as any).cancel();
    }

    const handleSearch = debounce(async () => {
      const { search, sort } = this.state;

      try {
        await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
          search,
          offset: 0,
          limit: pagination.getLimit(),
          sort,
        });
        routerService.push({ to: buildPaginatedPath(pathname, 1, pagination.getLimit()) });
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }, config.get('LIST_SEARCH_DELAY'));

    this.setState({
      searchDebounce: handleSearch,
      search: event.target.value,
    });

    handleSearch();
  }

  async handleDeleteApp(values, { setSubmitting }) {
    const {
      metaSites,
      insideApps,
      routerService,
      pathname,
      routeParams,
      metaSite,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );
    const { search, sort } = this.state;

    try {
      await metaSites.deleteAppFromMetaSite(values.metaSiteId, values.appId);
      await metaSites.fetchMetaSiteSettings(values.metaSiteId, {
        skipInitialState: true,
      });
      await metaSites.fetchMetaSiteIntegrity(values.metaSiteId);

      if (insideApps.length === 1 && pagination.getPage() > 1) {
        await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
          search,
          limit: pagination.getLimit(),
          offset: pagination.offset - pagination.getLimit(),
          sort,
        });
        routerService.push({
          to: buildPaginatedPath(pathname, pagination.getPage() - 1, pagination.getLimit()),
        });
      } else {
        await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
          search,
          limit: pagination.getLimit(),
          offset: pagination.offset,
          sort,
        });
      }
    /* eslint-disable-next-line */
    } catch (err) {}

    setSubmitting(false);
  }

  renderCell(isNegative, value, tooltipMessage) {
    const textComponent = (
      <Text dataId="TextStyle" color={isNegative ? 'Red' : undefined}>
        {value}
      </Text>
    );

    if (isNegative) {
      return (
        <Tooltip content={tooltipMessage}>
          {textComponent}
        </Tooltip>
      );
    }

    return textComponent;
  }

  renderAppRow(app) {
    const {
      userRole,
      metaSite,
      t,
    } = this.props;

    return (
      <List.Item
        key={app.id}
      >
        <InternalListLink
          to="/administration/apps/$appId"
          params={{ appId: app.id }}
          label={app.attributes.name}
        />
        {this.renderCell(
          metaSite.attributes.timezone !== app.attributes.timezone,
          app.attributes.timezone,
          t('meta-sites.details.settings.appsInside.timezone-tooltip'),
        )}
        <Text as="p">{app.attributes.currency}</Text>
        <Text dataId="TextStyle">
          {t(app.attributes.cnil ? 'meta-sites.details.settings.appsInside.cnil-on' : 'meta-sites.details.settings.appsInside.cnil-off')}
        </Text>
        {userRole === 'OWNER' && (
          <Form
            id="apps-in-meta-site-remove-app"
            initialValues={{
              metaSiteId: metaSite.id,
              appId: app.id,
            }}
            validationSchema={() => true}
            onSubmit={this.handleDeleteApp}
            render={({ isSubmitting, handleSubmit }) => (
              <Button
                icon="cross"
                appearance="subdued"
                ariaLabel={t('meta-sites.details.settings.appsInside.remove-app', { name: app.attributes.name })}
                disabled={isSubmitting}
                onClick={handleSubmit}
              />
            )}
          />
        )}
      </List.Item>
    );
  }

  render() {
    const {
      t,
      isLoading,
      insideApps,
      routeParams,
      config,
      failed,
      total,
      userRole,
    } = this.props;
    const {
      search,
      sort,
    } = this.state;

    const pagination = PaginationManager.fromQueryParams(
      insideApps,
      total,
      routeParams,
    );
    const columns: {
      name: string | React.ReactNode
      size: number
      align: 'left' | 'center' | 'right'
    }[] = [
      {
        name: t('meta-sites.details.settings.appsInside.column.websiteName'),
        size: 3,
        align: 'left',
      },
      {
        name: t('meta-sites.details.settings.appsInside.column.timezone'),
        size: 2,
        align: 'left',
      },
      {
        name: t('meta-sites.details.settings.appsInside.column.currency'),
        size: 2,
        align: 'left',
      },
      {
        name: (
          <List.SortableHeader
            order={sort.direction === '' ? 'asc' : 'dsc'}
            text={t('meta-sites.details.settings.appsInside.column.cnil')}
            onClick={() => this.handleSortingChange('cnil')}
            selected
          />
        ),
        size: 2,
        align: 'left',
      },
    ];

    if (userRole === 'OWNER') {
      columns.push({
        name: ' ',
        size: 1,
        align: 'right',
      });
    }

    return (
      <Stack vertical spacing="wide">
        <Stack.Item fill>
          <Stack vertical horizontalAlignment="middle">
            <Stack.Item fill>
              {!failed && (
                <StyledContainer
                  border={['left', 'right', 'top']}
                  padding="narrow"
                  backgroundColor="gray"
                >
                  <TextField
                    value={search}
                    name="search"
                    icon="search"
                    onChange={this.handleSearchChange}
                    placeholder={t('meta-site.details.settings.appsInside.search.placeholder')}
                    clearable
                  />
                </StyledContainer>
              )}
              <List
                columns={columns}
                isLoading={isLoading}
                error={failed}
              >
                {
                  !failed && insideApps.map(app =>
                    this.renderAppRow(app))
                }
              </List>
            </Stack.Item>
            {(!failed && insideApps.length > 0) && (
              <Stack.Item fill>
                <Pagination
                  onLimitChange={this.handleLimitChange}
                  limit={pagination.getLimit()}
                  total={total}
                  limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
                  startIndex={pagination.offset}
                  endIndex={pagination.offset + pagination.getLimit()}
                  onPageChange={e => this.handlePageChange(e, pagination)}
                />
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

const AppsConnected = connector(withInject<InjectedProps>({
  config: 'config',
  metaSites: 'MetaSitesCrate.metaSites',
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
})(
  errorBoundary(withTranslation(['administration'])(Apps)),
));

const AppsWrapper = (props) => {
  const routeParams = useParams({
    from: '/_secured/administration/meta-sites/$metaSiteId/settings/$page/$limit',
  });
  const { pathname } = useLocation();

  return (
    <AppsConnected
      {...props}
      routeParams={routeParams}
      pathname={pathname}
    />
  );
};

export default AppsWrapper;
